import React, { useRef } from 'react';
import './fill.less';
import { isNull, isString } from 'lodash';
import { formatColor } from '@meitu/saas-colors';
import { BrushType, IBrushAttribute, IBrushLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

const ColorItem: React.FC<{
    id: string;
    index: number;
    color: string;
    isGradient: boolean;
    onAfterChange?: Function;
}> = React.memo(({ id, index, isGradient, color, onAfterChange }) => {
    const originVal = useRef<string | null>(null);
    const colorGlobalWidgetRef = useGlobalWidget(editor, false);

    return (
        <div className="c-brush-panel__fills-item-wrap">
            <ColorGroup
                onOpenChange={(e) => {
                    colorGlobalWidgetRef.current = e;
                }}
                color={color}
                showIcon={false}
                setColor={(val) => {
                    if (originVal.current === null) {
                        originVal.current = color;
                    }
                    editor.brushLayer.setColor(id, val, false, index);
                }}
                setColorAfter={(val, __, { isBrandColor }) => {
                    if (isBrandColor) {
                        window._stat_track?.('hb_layers_func_use', {
                            func_id: '8_107',
                        });
                    }
                    if (originVal.current) {
                        editor.brushLayer.setColor(id, val, false, index, originVal.current);
                    }
                    originVal.current = null;
                    onAfterChange?.(77);
                }}
                isGradient={isGradient}
                popupOverlayClassName="c-brush-panel__color-picker"
                customPopupTrigger={
                    <div
                        style={{
                            width: 25,
                            height: 25,
                            cursor: 'pointer',
                            background: color,
                            border: '1px solid var(--stroke-system-border-overlay)',
                            borderRadius: 4,
                        }}
                    ></div>
                }
                onToggleBizTab={(bizTab) => {
                    if (bizTab === 'team') {
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '8_107',
                        });
                    }
                }}
            />
        </div>
    );
});

const SingleColorItem: React.FC<{
    id: string;
    color: string;
    isGradient: boolean;
    onAfterChange?: Function;
}> = React.memo(({ id, color, isGradient, onAfterChange }) => {
    const { $t } = useLocaleStore();
    const originVal = useRef<string | null>(null);
    const originId = useRef<string>('');
    const colorGlobalWidgetRef = useGlobalWidget(editor, false);

    return (
        <LabelAction title={$t((v) => v.panel.brushPanel.color)} align={true}>
            <ColorGroup
                onOpenChange={(e) => {
                    colorGlobalWidgetRef.current = e;
                }}
                popupTriggerSize={{
                    width: 78,
                    height: 32,
                }}
                color={color}
                setColor={(val) => {
                    if (originVal.current === null) {
                        originVal.current = color;
                        originId.current = id;
                    }
                    editor.brushLayer.setColor(id, val, false, 0);
                }}
                setColorAfter={(val, __, { isBrandColor }) => {
                    if (isBrandColor) {
                        window._stat_track?.('hb_layers_func_use', {
                            func_id: '8_107',
                        });
                    }
                    if (originVal.current) {
                        editor.brushLayer.setColor(originId.current, val, false, 0, originVal.current);
                    }
                    originVal.current = null;
                    if (!isBrandColor) {
                        // 品牌库不走通用的埋点逻辑
                        onAfterChange?.(77);
                    }
                }}
                isGradient={isGradient}
                popupOverlayClassName="c-brush-panel__color-picker"
                onToggleBizTab={(bizTab) => {
                    if (bizTab === 'team') {
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '8_107',
                        });
                    }
                }}
            />
        </LabelAction>
    );
});

export const FillFeature: React.FC<{
    id: string;
    isGradient: boolean;
    onAfterChange?: Function;
}> = React.memo(({ id, isGradient, onAfterChange }) => {
    const { $t } = useLocaleStore();

    const fills = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { fills } = layer.data as IBrushLayerProps;
        return fills;
    });

    if (isNull(fills)) return null;

    return (
        <div className="c-brush-panel__item ">
            {fills.length > 1 ? (
                <>
                    <LabelAction title={$t((v) => v.panel.brushPanel.color)} align={true}>
                        <div className="c-brush-panel__fills">
                            {fills.map(({ color }, index) => {
                                return (
                                    <ColorItem
                                        key={index}
                                        id={id}
                                        color={isString(color) ? color : (formatColor(color) as string)}
                                        index={index}
                                        isGradient={isGradient}
                                        onAfterChange={onAfterChange}
                                    />
                                );
                            })}
                        </div>
                    </LabelAction>
                </>
            ) : (
                <>
                    {fills[0]?.color ? (
                        <SingleColorItem
                            color={
                                isString(fills[0]?.color) ? fills[0]?.color : (formatColor(fills[0]?.color) as string)
                            }
                            id={id}
                            isGradient={isGradient}
                            onAfterChange={onAfterChange}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
});
